/**
 * Modul "Wochenabgabe": echte Wochenverwaltung. Die aktuelle Woche wird
 * direkt angezeigt; über "Neue Woche" wird die bisherige Woche abgeschlossen
 * (bleibt unverändert im Archiv erhalten) und eine neue Woche mit der
 * übernommenen Mitgliederliste der Vorwoche gestartet. Über "Alte Abgaben"
 * lassen sich vergangene, schreibgeschützte Wochen einsehen.
 *
 * Nutzt ausschließlich die bestehende Mitgliederverwaltung als Datengrundlage
 * (keine doppelte Mitgliederverwaltung) - Vorname, Nachname und Rang kommen
 * immer live aus "Mitglieder" (siehe WeeklyDuesForm).
 */
import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../../api/client";
import { usePermission } from "../../hooks/usePermission";
import { Card } from "../../components/ui/Card";
import { Badge } from "../../components/ui/Badge";
import { Modal } from "../../components/ui/Modal";
import { Spinner, EmptyState } from "../../components/ui/Spinner";
import { WeeklyDuesForm } from "./WeeklyDuesForm";
import type { WeeklyDue, WeeklyDuesWeek } from "../../types";

const STATUS_LABELS: Record<WeeklyDue["payment_status"], string> = {
  paid: "Bezahlt", unpaid: "Nicht bezahlt", open: "Offen", exempt: "Freigestellt", exempt_permanent: "Dauerhaft freigestellt",
};
const STATUS_TONE: Record<WeeklyDue["payment_status"], "success" | "danger" | "warning" | "accent" | "gray"> = {
  paid: "success", unpaid: "danger", open: "warning", exempt: "accent", exempt_permanent: "gray",
};

function formatRange(start: string, end: string) {
  const fmt = (d: string) => new Date(d).toLocaleDateString("de-DE");
  return `${fmt(start)} – ${fmt(end)}`;
}

type View = "current" | "archive" | "week";

export default function WeeklyDues() {
  const canManage = usePermission("weeklydues.manage");
  const canSeePrepaid = usePermission("weeklydues.prepaid");
  const canDeleteWeeks = usePermission("weeklydues.delete_weeks");
  const [deletingWeekId, setDeletingWeekId] = useState<number | null>(null);

  const [view, setView] = useState<View>("current");

  const [currentWeekData, setCurrentWeekData] = useState<{ week: WeeklyDuesWeek | null; entries: WeeklyDue[] }>({ week: null, entries: [] });
  const [archiveWeeks, setArchiveWeeks] = useState<WeeklyDuesWeek[]>([]);
  const [openWeek, setOpenWeek] = useState<{ week: WeeklyDuesWeek; entries: WeeklyDue[] } | null>(null);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [hideExempt, setHideExempt] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<WeeklyDue | null | undefined>(undefined);
  const [addingMember, setAddingMember] = useState(false);
  const [newWeekModalOpen, setNewWeekModalOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);

  const loadCurrent = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.get<{ week: WeeklyDuesWeek | null; entries: WeeklyDue[] }>("/api/weekly-dues/current");
      setCurrentWeekData(result);
    } finally {
      setLoading(false);
    }
  }, []);

  // Gleicht die aktuelle Woche mit der Mitgliederverwaltung ab (neu
  // angelegte Mitglieder, die noch nicht in dieser Woche stehen) - über den
  // Button manuell auslösbar sowie automatisch alle 60 Sekunden im
  // Hintergrund (siehe Effect weiter unten), analog zu den bestehenden
  // Discord-Sync-Modulen ("Lagersystem"/"Sanktionssystem"). `silent`
  // unterdrückt die Erfolgsmeldung, wenn es nichts zu ergänzen gab, damit der
  // automatische 60-Sekunden-Sync nicht ständig eine Meldung einblendet.
  const syncMembers = useCallback(async (silent: boolean) => {
    setSyncing(true);
    if (!silent) setSyncMessage(null);
    try {
      const result = await api.post<{ added: number }>("/api/weekly-dues/sync-members");
      if (result.added > 0) {
        setSyncMessage(`Synchronisiert: ${result.added} neue(s) Mitglied(er) aus der Mitgliederverwaltung ergänzt.`);
        await loadCurrent();
      } else if (!silent) {
        setSyncMessage("Synchronisiert: keine neuen Mitglieder.");
      }
    } catch (err) {
      if (!silent) setSyncMessage(err instanceof ApiError ? err.message : "Synchronisierung fehlgeschlagen.");
    } finally {
      setSyncing(false);
    }
  }, [loadCurrent]);

  const loadArchive = useCallback(async () => {
    setLoading(true);
    try {
      const weeks = await api.get<WeeklyDuesWeek[]>("/api/weekly-dues/weeks");
      setArchiveWeeks(weeks);
    } finally {
      setLoading(false);
    }
  }, []);

  const openArchivedWeek = useCallback(async (weekId: number) => {
    setLoading(true);
    try {
      const result = await api.get<{ week: WeeklyDuesWeek; entries: WeeklyDue[] }>(`/api/weekly-dues/weeks/${weekId}`);
      setOpenWeek(result);
      setView("week");
    } finally {
      setLoading(false);
    }
  }, []);

  // Archiv aufräumen (Recht "weeklydues.delete_weeks", ab Werk nur President):
  // löscht eine abgeschlossene Woche samt Einträgen endgültig.
  async function handleDeleteWeek(w: WeeklyDuesWeek) {
    if (!confirm(
      `Woche "${w.label}" (${w.member_count ?? 0} Einträge) wirklich endgültig löschen?

Alle Einträge dieser Woche gehen verloren. Das kann nicht rückgängig gemacht werden.`
    )) return;
    setDeletingWeekId(w.id);
    try {
      await api.delete(`/api/weekly-dues/weeks/${w.id}`);
      await loadArchive();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "Löschen fehlgeschlagen.");
    } finally {
      setDeletingWeekId(null);
    }
  }

  useEffect(() => {
    if (view === "current") loadCurrent();
    if (view === "archive") loadArchive();
  }, [view, loadCurrent, loadArchive]);

  // Automatischer Sync mit der Mitgliederverwaltung alle 60 Sekunden, solange
  // die aktuelle Woche angezeigt wird - nur für Rollen mit "weeklydues.manage"
  // (der Endpunkt ist entsprechend gesperrt) und nur, wenn bereits eine
  // Woche läuft (sonst würde der Endpunkt einen 409-Fehler liefern).
  useEffect(() => {
    if (view !== "current" || !canManage || !currentWeekData.week) return;
    const interval = setInterval(() => { syncMembers(true); }, 60_000);
    return () => clearInterval(interval);
  }, [view, canManage, currentWeekData.week, syncMembers]);

  async function handleDelete(entry: WeeklyDue) {
    if (!confirm(`Eintrag von "${entry.first_name} ${entry.last_name}" wirklich löschen?`)) return;
    try {
      await api.delete(`/api/weekly-dues/${entry.id}`);
      loadCurrent();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "Löschen fehlgeschlagen.");
    }
  }

  // Kleine Übersicht oben ("wie viele haben bezahlt, wie viele sind noch
  // offen") - immer über ALLE Mitglieder der aktuellen Woche berechnet,
  // unabhängig von Suche/Status-Filter/"Freigestellte ausblenden".
  const paidCount = currentWeekData.entries.filter((e) => e.payment_status === "paid").length;
  const openCount = currentWeekData.entries.filter((e) => e.payment_status === "open").length;
  const unpaidCount = currentWeekData.entries.filter((e) => e.payment_status === "unpaid").length;
  const exemptCount = currentWeekData.entries.filter((e) => e.payment_status === "exempt").length;
  const permanentExemptCount = currentWeekData.entries.filter((e) => e.payment_status === "exempt_permanent").length;

  const currentEntries = currentWeekData.entries.filter((e) => {
    if (hideExempt && e.payment_status === "exempt") return false;
    if (statusFilter && e.payment_status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return e.first_name.toLowerCase().includes(q) || e.last_name.toLowerCase().includes(q) || (e.item_description ?? "").toLowerCase().includes(q);
    }
    return true;
  });

  function renderEntriesTable(entries: WeeklyDue[], readOnly: boolean) {
    if (entries.length === 0) return <EmptyState message="Keine Einträge vorhanden." />;
    return (
      <table className="table-base">
        <thead>
          <tr>
            <th>Vorname</th>
            <th>Nachname</th>
            <th>Gegenstand</th>
            <th>Bezahlt ?</th>
            {!readOnly && canManage && <th>Aktionen</th>}
          </tr>
        </thead>
        <tbody>
          {entries.map((e) => (
            <tr key={e.id}>
              <td className="font-medium text-gray-100">{e.first_name}</td>
              <td className="font-medium text-gray-100">{e.last_name}</td>
              <td className="text-gray-400">{e.item_description ?? "—"}</td>
              <td>
                <Badge tone={STATUS_TONE[e.payment_status]}>{STATUS_LABELS[e.payment_status]}</Badge>
                {canSeePrepaid && e.prepaid_weeks > 0 && (
                  <span className="ml-2 text-xs text-gray-400" title="Im Voraus bezahlt - verringert sich bei jeder neuen Woche um 1">
                    +{e.prepaid_weeks} {e.prepaid_weeks === 1 ? "Woche" : "Wochen"} im Voraus
                  </span>
                )}
              </td>
              {!readOnly && canManage && (
                <td className="text-right whitespace-nowrap">
                  <button className="text-accent text-xs hover:underline mr-3" onClick={() => setEditing(e)}>Bearbeiten</button>
                  <button className="text-danger text-xs hover:underline" onClick={() => handleDelete(e)}>Löschen</button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-100">Wochenabgabe</h1>
          {view === "current" && (
            <>
              <p className="text-sm text-gray-500 mt-1">
                {currentWeekData.week
                  ? <>Aktuelle Woche: <span className="text-accent font-medium">{currentWeekData.week.label}</span> ({formatRange(currentWeekData.week.start_date, currentWeekData.week.end_date)})</>
                  : "Es wurde noch keine Woche gestartet."}
              </p>
              {currentWeekData.week && (
                <p className="text-xs text-gray-500 mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span><span className="text-success font-semibold">{paidCount}</span> bezahlt</span>
                  <span><span className="text-warning font-semibold">{openCount}</span> offen</span>
                  <span><span className="text-danger font-semibold">{unpaidCount}</span> nicht bezahlt</span>
                  <span><span className="text-accent font-semibold">{exemptCount}</span> freigestellt</span>
                  <span><span className="text-gray-300 font-semibold">{permanentExemptCount}</span> dauerhaft freigestellt</span>
                </p>
              )}
            </>
          )}
          {view === "archive" && <p className="text-sm text-gray-500 mt-1">Vergangene, schreibgeschützte Wochen.</p>}
          {view === "week" && openWeek && (
            <p className="text-sm text-gray-500 mt-1">
              {openWeek.week.label} ({formatRange(openWeek.week.start_date, openWeek.week.end_date)}) — Archiv, schreibgeschützt
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {view !== "current" && <button className="btn-secondary" onClick={() => setView("current")}>← Aktuelle Woche</button>}
          {view !== "archive" && <button className="btn-secondary" onClick={() => setView("archive")}>Alte Abgaben</button>}
          {canManage && view === "current" && currentWeekData.week && (
            <button className="btn-secondary whitespace-nowrap" onClick={() => syncMembers(false)} disabled={syncing}>
              {syncing ? "Synchronisiere…" : "🔄 Mit Mitgliedern synchronisieren"}
            </button>
          )}
          {canManage && view === "current" && (
            <button className="btn-primary" onClick={() => setNewWeekModalOpen(true)}>+ Neue Woche</button>
          )}
        </div>
      </div>

      {view === "current" && (
        <>
          {syncMessage && (
            <div className="bg-surface-raised text-gray-300 text-sm rounded-lg px-3 py-2">{syncMessage}</div>
          )}
          {!currentWeekData.week && !loading ? (
            <Card className="p-6 text-center space-y-3">
              <p className="text-gray-400">Es wurde noch keine Woche gestartet. Starten Sie die erste Wochenabgabe, um Mitglieder zu erfassen.</p>
              {canManage && <button className="btn-primary" onClick={() => setNewWeekModalOpen(true)}>+ Neue Woche</button>}
            </Card>
          ) : (
            <>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <h2 className="font-display text-base font-semibold text-gray-100 uppercase tracking-wide">Mitglieder in dieser Woche</h2>
                {canManage && <button className="btn-primary" onClick={() => setAddingMember(true)}>+ Mitglied hinzufügen</button>}
              </div>

              <Card className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <input
                    className="input sm:max-w-xs"
                    placeholder="Suche nach Name oder Gegenstand…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  <select className="input sm:max-w-[200px]" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                    <option value="">Alle Status</option>
                    <option value="paid">Bezahlt</option>
                    <option value="unpaid">Nicht bezahlt</option>
                    <option value="open">Offen</option>
                    <option value="exempt">Freigestellt</option>
                    <option value="exempt_permanent">Dauerhaft freigestellt</option>
                  </select>
                  <label className="flex items-center gap-2 text-sm text-gray-300 whitespace-nowrap cursor-pointer select-none sm:ml-1">
                    <input type="checkbox" checked={hideExempt} onChange={(e) => setHideExempt(e.target.checked)} />
                    Freigestellte ausblenden
                  </label>
                </div>
              </Card>

              <Card padded={false} className="overflow-x-auto">
                {loading ? <Spinner /> : renderEntriesTable(currentEntries, false)}
              </Card>
            </>
          )}
        </>
      )}

      {view === "archive" && (
        <Card padded={false} className="overflow-x-auto">
          {loading ? (
            <Spinner />
          ) : archiveWeeks.length === 0 ? (
            <EmptyState message="Noch keine abgeschlossenen Wochen vorhanden." />
          ) : (
            <table className="table-base">
              <thead>
                <tr>
                  <th>Kalenderwoche</th>
                  <th>Zeitraum</th>
                  <th>Mitglieder</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {archiveWeeks.map((w) => (
                  <tr key={w.id}>
                    <td className="font-medium text-gray-100">{w.label}</td>
                    <td className="text-gray-400">{formatRange(w.start_date, w.end_date)}</td>
                    <td className="text-gray-400">{w.member_count ?? 0}</td>
                    <td>{w.is_current ? <Badge tone="accent">Aktuell</Badge> : <Badge tone="gray">Abgeschlossen</Badge>}</td>
                    <td className="text-right">
                      <button className="text-accent text-xs hover:underline" onClick={() => openArchivedWeek(w.id)}>Öffnen</button>
                      {canDeleteWeeks && !w.is_current && (
                        <button
                          className="text-danger text-xs hover:underline ml-3 disabled:opacity-40"
                          disabled={deletingWeekId !== null}
                          onClick={() => handleDeleteWeek(w)}
                        >
                          {deletingWeekId === w.id ? "Lösche…" : "Löschen"}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}

      {view === "week" && openWeek && (
        <Card padded={false} className="overflow-x-auto">
          {loading ? <Spinner /> : renderEntriesTable(openWeek.entries, true)}
        </Card>
      )}

      {editing !== undefined && canManage && (
        <WeeklyDuesForm
          entry={editing}
          onClose={() => setEditing(undefined)}
          onSaved={() => { setEditing(undefined); loadCurrent(); }}
        />
      )}

      {addingMember && canManage && (
        <WeeklyDuesForm
          entry={null}
          excludeMemberIds={currentWeekData.entries.map((e) => e.member_id)}
          onClose={() => setAddingMember(false)}
          onSaved={() => { setAddingMember(false); loadCurrent(); }}
        />
      )}

      {newWeekModalOpen && (
        <NewWeekModal
          currentLabel={currentWeekData.week?.label ?? null}
          onClose={() => setNewWeekModalOpen(false)}
          onDone={() => { setNewWeekModalOpen(false); setView("current"); loadCurrent(); }}
        />
      )}
    </div>
  );
}

/**
 * Eigenständiges Bestätigungs-Modal statt window.confirm() - native
 * Browser-Dialoge verhalten sich browser-/geräteübergreifend inkonsistent
 * (z.B. auf Mobilgeräten oder in eingebetteten Browsern), daher wie bei
 * "Gesamten Lagerbestand zurücksetzen" ein eigenes, zuverlässiges Modal.
 */
function NewWeekModal({ currentLabel, onClose, onDone }: { currentLabel: string | null; onClose: () => void; onDone: () => void }) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleConfirm() {
    setSubmitting(true);
    setError(null);
    try {
      await api.post("/api/weekly-dues/weeks", {});
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Neue Woche konnte nicht gestartet werden.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title="Neue Woche starten"
      onClose={() => !submitting && onClose()}
      footer={
        <>
          <button className="btn-secondary" onClick={onClose} disabled={submitting}>Abbrechen</button>
          <button className="btn-primary" onClick={handleConfirm} disabled={submitting}>
            {submitting ? "Wird gestartet…" : "Neue Woche starten"}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <div className="bg-danger-muted text-danger text-sm rounded-lg px-3 py-2">{error}</div>}
        <p className="text-gray-200 text-sm">
          {currentLabel
            ? <>Die aktuelle Woche <span className="font-semibold text-gray-100">„{currentLabel}"</span> wird abgeschlossen und bleibt unverändert im Archiv erhalten. Die Mitgliederliste wird für die neue Woche übernommen - Gegenstand und Zahlungsstatus werden dabei zurückgesetzt (außer bei „Dauerhaft freigestellt" - dieser Status bleibt erhalten - sowie bei im Voraus bezahlten Wochen: diese Mitglieder gelten automatisch als „Bezahlt").</>
            : <>Es wird die erste Wochenabgabe gestartet. Die aktuelle Mitgliederliste wird als Startliste übernommen.</>}
        </p>
      </div>
    </Modal>
  );
}
